import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { WfToast } from './services/notifications.service';
import { TOAST_TYPES_MAP } from './const/toast-types.map';
import { ClipboardService } from 'ngx-clipboard';
import { ProductEventTrackingService } from 'wflow-features/product-analytics';
import { IconsModule, WuiButtonModule } from 'wflow-ui';

@Component({
  selector: 'wf-notification-toast',
  imports: [IconsModule, WuiButtonModule],
  template: `
    <div
      class="wf-toast"
      [class.wf-toast--success]="toast().type === 'success'"
      [class.wf-toast--error]="toast().type === 'error'"
      [class.wf-toast--warning]="toast().type === 'warning'"
      [class.wf-toast--info]="toast().type === 'info'"
      (click)="onTapClick($event)"
    >
      @if (isLast() || (!isLast() && paused())) {
        <div class="wf-toast__content">
          <div class="wf-toast__header">
            <wf-icon
              [iconName]="iconStyles().icon"
              [iconClass]="iconStyles().iconClass + ' xs'"
              solid
            />

            <div class="wf-toast__title">
              <span
                class="wf-text-sm-demibold"
                [innerHTML]="toast().title"
              ></span>
            </div>
          </div>

          @if (toast().message) {
            <span
              class="wf-toast__message wf-text-sm-regular"
              [innerHTML]="toast().message"
            ></span>
          }
        </div>

        <div class="wf-toast__actions">
          @if (toast().closeButton) {
            <button
              wui-button
              type="button"
              size="xs"
              (click)="onCloseClick($event)"
            >
              <wf-icon iconName="x-close" iconClass="grey-icon" />
            </button>
          } @else {
            <div></div>
          }

          @if (toast().type === 'error') {
            <button
              wui-button
              type="button"
              size="xs"
              (click)="copyToClipboard($event)"
            >
              <wf-icon
                [iconName]="copied() ? 'check' : 'copy-02'"
                [iconClass]="copied() ? 'green' : ''"
              />
            </button>
          }
        </div>
      }

      @if (toast().progressBar && toast().duration && toast().duration > 0) {
        <div class="wf-toast__progress-track"></div>
        <div
          class="wf-toast__progress"
          [class.wf-toast__progress--paused]="paused()"
          [style.animation-duration.ms]="toast().duration"
        ></div>
      }
    </div>
  `,
  styles: [
    `
      .wf-toast {
        position: relative;
        display: flex;
        width: 300px;
        min-height: 45px;
        max-height: 150px;
        padding: var(--wf-spacer-sm) var(--wf-spacer-md);
        padding-bottom: calc(var(--wf-spacer-sm) + var(--wf-spacer-xs));
        border: 1px solid var(--wf-border-utility-modal);

        background: var(--wf-background-modal);
        border-radius: var(--wf-round-corner-xs);
        box-shadow: var(--wf-shadow-lg);
        cursor: pointer;
        pointer-events: auto;
        overflow: hidden;

        &--success {
          padding-bottom: var(--wf-spacer-sm);

          & .wf-toast__progress {
            background-color: var(--success-color);
          }

          & .wf-toast__progress-track {
            background-color: var(--wf-background-feedback-success);
          }
        }

        &--error {
          & .wf-toast__progress {
            background-color: var(--error-color);
          }

          & .wf-toast__progress-track {
            background-color: var(--wf-background-feedback-error);
          }
        }

        &--warning {
          & .wf-toast__progress {
            background-color: var(--warn-color);
          }

          & .wf-toast__progress-track {
            background-color: var(--wf-background-feedback-warning);
          }
        }

        &--info {
          & .wf-toast__progress {
            background-color: var(--accent-color);
          }

          & .wf-toast__progress-track {
            background-color: var(--wf-background-feedback-info-2);
          }
        }
      }

      .wf-toast__actions {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        & button {
          width: 16px;
          min-width: 16px;
          height: 16px;
          padding: 12px;
          margin: -2px;
        }
      }

      .wf-toast__content {
        display: flex;
        flex-direction: column;
        flex: 1;
      }

      .wf-toast__header {
        display: flex;
        gap: var(--wf-spacer-xs);
        width: 100%;
      }

      .wf-toast__title {
        flex: 1;
        min-width: 0;

        display: flex;
        align-items: center;
      }

      .wf-toast__message {
        --max-lines: 4;

        color: var(--wf-foreground-secondary);
        display: -webkit-box;
        -webkit-line-clamp: var(--max-lines);
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-word;
      }

      .wf-toast__progress,
      .wf-toast__progress-track {
        position: absolute;
        bottom: 0;
        left: 0;
        height: var(--wf-spacer-xs);
        width: 100%;
      }

      .wf-toast__progress {
        transform-origin: left;
        animation: shrink linear;
        z-index: 1;

        &--paused {
          animation-play-state: paused;
        }
      }

      @keyframes shrink {
        from {
          transform: scaleX(1);
        }
        to {
          transform: scaleX(0);
        }
      }

      .wf-toast__progress-track {
        z-index: 0;
      }
    `,
  ],
})
export class WfNotificationToastComponent implements OnDestroy {
  private readonly clipboardService = inject(ClipboardService);
  private readonly productTrackingSvc = inject(ProductEventTrackingService);
  private copyTimeoutId?: ReturnType<typeof setTimeout>;

  /** When this toast appeared, for the dwell-before-dismiss measurement. */
  private readonly shownAt = Date.now();

  readonly toast = input.required<WfToast>();
  readonly paused = input<boolean>(false);
  readonly isLast = input<boolean>(false);
  readonly dismiss = output<number>();

  readonly iconStyles = computed(() => TOAST_TYPES_MAP[this.toast().type]);
  readonly copied = signal<boolean>(false);

  onCloseClick(event: MouseEvent): void {
    event.stopPropagation();
    this.dismissToast();
  }

  onTapClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.toast().tapToDismiss) {
      this.dismissToast();
    }
  }

  /**
   * Area P [Frequency] + [Frustration] — the user closed the notification by
   * hand. Both keys are error-only: `comp_error_dismissed` says which error was
   * cleared without acting on it, `comp_error_banner_dwell` how long it sat on
   * screen first (from this toast appearing to the dismiss click). A toast that
   * simply times out is not a dismissal and reports neither.
   */
  private dismissToast(): void {
    const toast = this.toast();

    if (toast.type === 'error') {
      const errorKey = toast.errorKey ?? 'unknown';
      const secondsVisible = Math.round((Date.now() - this.shownAt) / 1000);

      this.productTrackingSvc.track('comp_error_dismissed', errorKey);
      this.productTrackingSvc.track('comp_error_banner_dwell', secondsVisible);
    }

    this.dismiss.emit(toast.id);
  }

  copyToClipboard(event: MouseEvent) {
    event.stopPropagation();
    if (this.copied()) return;
    const date = new Date().toISOString();

    const message = [
      this.toast().title,
      this.toast().message,
      date,
      this.toast().errorJson,
    ]
      .filter(Boolean)
      .join('\n\n');

    this.clipboardService.copy(message);

    // Area P [Frustration] — the error was worth copying (to report or escalate).
    // The copy button only renders on error toasts, so no type guard is needed.
    this.productTrackingSvc.track(
      'comp_error_toast_copied',
      this.toast().errorKey ?? 'unknown',
    );

    this.copied.set(true);
    this.copyTimeoutId = setTimeout(() => this.copied.set(false), 3000);
  }

  ngOnDestroy(): void {
    if (this.copyTimeoutId) {
      clearTimeout(this.copyTimeoutId);
    }
  }
}
